// @ts-nocheck
import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { data } from 'src/assets/demo/data';
import { ROOT } from 'src/constants/routes';
import { useLongPress } from 'src/hooks/use-long-press';
import { prayerHubs, prayerHubs as prayerHubsApi } from 'src/store/api/prayer-hubs';
import prayerStats from 'src/utils/stats';

import { createMainTimeline, createPointsScreenTimeline } from './animations/timeline';
import { ActionButton } from './components/action-button';
import { BackgroundGlow } from './components/background-glow';
import { EncouragementMessage } from './components/encouragement-message';
import { FloatingBlob } from './components/floating-blob';
import { GlowCircle } from './components/glow-circle';
import { HelloText } from './components/hello-text';
import { MainPrayerSection } from './components/main-prayer-section';
import { PointsScreen } from './components/points-screen';
import { PrayerPreText } from './components/prayer-pre-text';
import { PrayerSection } from './components/prayer-section';
import { Universe } from './components/universe';
import { useAnimationRefs } from './hooks/use-animation-refs';
import { useViewport } from './hooks/use-viewport';
import { prayerDispatcher } from './prayer-dispatcher';
import css from './styles.module.scss';

export default function PrayerPage() {
  const navigate = useNavigate();
  const refs = useAnimationRefs();
  const viewport = useViewport();
  const params = useParams();
  const tl = useRef<gsap.core.Timeline | null>(null);
  const tlForPointsScreen = useRef<gsap.core.Timeline | null>(null);
  const { data } = prayerHubsApi.endpoints.getPrayerHub.useQuery({ id: params.id ?? '' });

  const prayer = data?.success ? data.data : null;
  const prayerPages = splitTextBySentences(prayer?.prayer?.content.he.replaceAll('${name}', prayer.personName) ?? '');

  const longPressEvents = useLongPress({
    onStart: () => {
      try {
        prayerStats.startPrayer(params.id);
      } catch {
        // ignore (storage can fail in some environments)
      }
      tl.current?.play();
    },
    onCancel: () => {
      if (tl.current && tl.current.time() < 3) tl.current.reverse();
    },
    delay: 3000,
  });

  useEffect(() => {
    prayerDispatcher.subscribe('prayerScreenVisibility', onPrayerScreenVisibilityChange);

    function onPrayerScreenVisibilityChange(isVisible: boolean) {
      if (isVisible && prayerPages.length > 1) {
        tl.current?.pause();
      }
    }

    return () => {
      prayerDispatcher.unsubscribe('prayerScreenVisibility', onPrayerScreenVisibilityChange);
    };
    // TODO: add dependencies when will be real data fetching
  }, []);

  useEffect(() => {
    // fetch full prayer hub data

    const ctx = gsap.context(() => {
      tl.current = createMainTimeline(refs, viewport);
      tlForPointsScreen.current = createPointsScreenTimeline(refs, viewport);
    });

    return () => {
      tl.current?.kill();
      tlForPointsScreen.current?.kill();
      ctx.revert();
    };
  }, []);

  const onFinishPrayBtnClick = () => {
    if (tl.current?.paused) {
      tl.current?.play();
    }
    tlForPointsScreen.current?.play();
  };

  return (
    <div ref={refs.container} className={css.prayerPage}>
      <div className={css.backLink} onClick={() => navigate(-1)}>
        <svg width="7" height="11" viewBox="0 0 7 11" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M2.4158 5.2458L5.9558 1.7058C6.14205 1.51844 6.24659 1.26498 6.24659 1.0008C6.24659 0.736612 6.14205 0.483161 5.9558 0.295798C5.86283 0.20207 5.75223 0.127675 5.63037 0.0769067C5.50852 0.026138 5.37781 0 5.2458 0C5.11379 0 4.98308 0.026138 4.86122 0.0769067C4.73936 0.127675 4.62876 0.20207 4.5358 0.295798L0.295798 4.5358C0.20207 4.62876 0.127676 4.73936 0.0769072 4.86122C0.0261385 4.98308 0 5.11379 0 5.2458C0 5.37781 0.0261385 5.50852 0.0769072 5.63037C0.127676 5.75223 0.20207 5.86283 0.295798 5.9558L4.5358 10.2458C4.62924 10.3385 4.74005 10.4118 4.86189 10.4616C4.98373 10.5113 5.11419 10.5366 5.2458 10.5358C5.3774 10.5366 5.50787 10.5113 5.6297 10.4616C5.75154 10.4118 5.86236 10.3385 5.9558 10.2458C6.14205 10.0584 6.24659 9.80498 6.24659 9.5408C6.24659 9.27661 6.14205 9.02316 5.9558 8.8358L2.4158 5.2458Z"
            fill="white"
          />
        </svg>
      </div>
      <BackgroundGlow />

      <FloatingBlob ref={refs.blobs.cyan} variant="cyan" />
      <FloatingBlob ref={refs.blobs.pink} variant="pink" />

      <HelloText ref={refs.texts.hello} />

      <PrayerPreText ref={refs.texts.prayerPre} />

      <EncouragementMessage
        encouragementMsgContainerRef={refs.prayer.encouragementMsgContainerRef}
        content={prayer?.encouragementMessage}
      />

      <PrayerSection
        prayerContainerRef={refs.prayer.container}
        prayerTitleRef={refs.prayer.title}
        prayerContentRef={refs.prayer.content}
        prayerInfoRef={refs.prayer.info}
        psalmsChapter={prayer?.psalmsChapter}
      />

      <MainPrayerSection
        prayer={prayer?.prayer?.content.he.replaceAll('${name}', prayer.personName) ?? ''}
        prayerMainListRef={refs.mainPrayer.listRef}
        confirmBtnRef={refs.buttons.confirm}
        prayerMainContainerRef={refs.mainPrayer.container}
        onConfirmBtnClick={onFinishPrayBtnClick}
      />

      <GlowCircle ref={refs.glowCircle} />

      <Universe
        ref={refs.universe}
        star1Ref={refs.stars[0]}
        star2Ref={refs.stars[1]}
        star3Ref={refs.stars[2]}
        star4Ref={refs.stars[3]}
        star5Ref={refs.stars[4]}
        star6Ref={refs.stars[5]}
        star7Ref={refs.stars[6]}
        circleRef={refs.circle}
      />

      <ActionButton decorRef={refs.buttons.decor} ref={refs.buttons.action} {...longPressEvents} />

      <PointsScreen ref={refs.pointsScreen} prayerHubId={params.id} />
    </div>
  );
}

function splitTextBySentences(text: string, minLength = 100, maxLength = 160) {
  const sentences = text.match(/[^.!?。！？…]+[.!?。！？…]*/g) || [];

  const chunks = [];
  let currentChunk = '';

  for (const sentence of sentences) {
    const trimmed = sentence.trim();

    if (!trimmed) continue;

    const candidate = currentChunk ? `${currentChunk} ${trimmed}` : trimmed;

    if (currentChunk.length < minLength || candidate.length <= maxLength) {
      currentChunk = candidate;
      continue;
    }

    chunks.push(currentChunk);

    if (trimmed.length > maxLength) {
      const words = trimmed.split(' ');
      let temp = '';

      for (const word of words) {
        const next = temp ? `${temp} ${word}` : word;

        if (next.length <= maxLength) {
          temp = next;
        } else {
          if (temp) chunks.push(temp);
          temp = word;
        }
      }

      currentChunk = temp;
    } else {
      currentChunk = trimmed;
    }
  }

  if (currentChunk) {
    chunks.push(currentChunk);
  }

  return chunks;
}
